/* eslint-disable  @typescript-eslint/no-explicit-any */
import { json } from "@codemirror/lang-json";
import {
  Alert,
  AlertColor,
  Autocomplete,
  Breadcrumbs,
  Button,
  Chip,
  Grid,
  MenuItem,
  Snackbar,
  TextField,
} from "@mui/material";
import CodeMirror from "@uiw/react-codemirror";
import { tfidHighlightExtension } from "utils/TfidCodemirrorExtension";
import { LoadingOverlay } from "components/LoadingOverlay";
import { useContext, useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { DataStructureCategory, IDataEntity, IDataStructure, JSONSchema } from "../../services/backend/DataSchemaService";
import { useDataPersistenceService } from "../../services/backend/DataService";
import { AppContext } from "utils/AppContext";
import RefreshIcon from "@mui/icons-material/Refresh";
import SaveAltIcon from "@mui/icons-material/SaveAlt";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import PlaylistAddIcon from "@mui/icons-material/PlaylistAdd";
import PublishIcon from "@mui/icons-material/Publish";
import { DataExportDialog } from "./DataExportDialog";
import { DataImportDialog } from "./DataImportDialog";
import ArchiveIcon from "@mui/icons-material/Archive";
import UndoIcon from "@mui/icons-material/Undo";
import LaunchIcon from "@mui/icons-material/Launch";
import StorageIcon from "@mui/icons-material/Storage";
import TerminalIcon from "@mui/icons-material/Terminal";
import ManageSearchIcon from "@mui/icons-material/ManageSearch";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import { RedisTerminal } from "./RedisTerminal";
import { DeepSearchDialog } from "./DeepSearchDialog";
import { CloneEntitiesDialog } from "./CloneEntitiesDialog";

interface DataEntityLink {
  dataStructure: string;
  id: string;
  showId?: boolean;
}

// empty data-structure for initialization and reset
const defaultDataStructure: IDataStructure = {
  name: "",
  creationDate: 0,
  category: DataStructureCategory.KEY_VALUE,
  tfidType: 0,
  persistenceLayers: [],
  archived: false,
  schema: {} as JSONSchema,
  version: 0,
  readAccessGrants: [],
  writeAccessGrants: [],
};

// map of data-structures and their linked entities by specific fields
// these don't have the relationship defined through the description
const linkedEntitiesMap = {
  Player: [
    {
      dataStructure: "Neighborhood",
      field: "neighborhoodId",
      type: "string",
    },
  ],
  Neighborhood: [
    {
      dataStructure: "Player",
      field: "playerIds",
      type: "array",
    },
  ],
};

const DataEntityEditor = () => {
  const dataPersistenceService = useDataPersistenceService();
  const [entityContent, setEntityContent] = useState("");
  const [entity, setEntity] = useState<IDataEntity | Record<string, never>>({});
  const context = useContext(AppContext);
  const [structures, setStructures] = useState<IDataStructure[]>([]);
  const [entitiesIds, setEntitiesIds] = useState<string[]>([]);
  const [selectedStructure, setSelectedStructure] = useState<IDataStructure>(defaultDataStructure);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [alertMessage, setAlertMessage] = useState<string>("");
  const [alertSeverity, setAlertSeverity] = useState<AlertColor>("info");
  const [idValue, setIdValue] = useState<string>("");
  const [inputIdValue, setInputIdValue] = useState("");
  const [exportIds, setExportIds] = useState<{ id: string; name: string }[]>([]);
  const [showExportList, setShowExportList] = useState<boolean>(false);
  const [showImportOptions, setShowImportOptions] = useState<boolean>(false);
  const [showRedisTerminal, setShowRedisTerminal] = useState<boolean>(false);
  const [showDeepSearch, setShowDeepSearch] = useState<boolean>(false);
  const [showCloneEntities, setShowCloneEntities] = useState<boolean>(false);
  const appContext = useContext(AppContext);
  const [linkedEntities, setLinkedEntities] = useState<DataEntityLink[]>([]);

  const [searchParams] = useSearchParams();
  const [history, setHistory] = useState<DataEntityLink[]>([]);
  const historySize = 5;

  const alertCloseHandler = (event?: React.SyntheticEvent | Event, reason?: string) => {
    if (reason === "clickaway") {
      return;
    }
    setAlertMessage("");
  };

  const clearStates = () => {
    setEntityContent("");
    setIdValue("");
    setInputIdValue("");
    setStructures([]);
    setEntitiesIds([]);
    setSelectedStructure(defaultDataStructure);
    setExportIds([]);
    setLinkedEntities([]);
  };

  const loadMainBranch = async () => {
    try {
      setIsLoading(true);
      clearStates();
      const { list } = await dataPersistenceService.listDataStructures();
      setStructures(list);
    } catch (e: any) {
      setAlertMessage(e.debugMessage || "Could not load data-schema structures.");
      setAlertSeverity("error");
    } finally {
      setIsLoading(false);
    }
  };

  const loadDataEntityFromQueryParams = async () => {
    const ds = searchParams.get("data_structure");
    const id = searchParams.get("id");
    if (ds && id) {
      const structure = structures.find((s) => s.name === ds);
      if (structure) {
        setSelectedStructure(structure);
        await loadDataEntitiesIds(structure);
        setIdValue(id);
        setInputIdValue(id);
        await loadEntity(ds, id);
      }
    }
  };

  const loadDataEntitiesIds = async (structure: IDataStructure | null) => {
    try {
      if (structure) {
        setEntity({});
        setEntityContent("");
        setIdValue("");
        setInputIdValue("");
        setIsLoading(true);
        const { list } = await dataPersistenceService.listDataEntitiesId(structure.name);
        setEntitiesIds(list);
      }
    } catch (e: any) {
      setEntitiesIds([]);
      setAlertMessage(e.debugMessage || "Could not load data entities ids");
      setAlertSeverity("error");
    } finally {
      setIsLoading(false);
    }
  };

  const codeMirrorChangeHandler = (value: string) => {
    setEntityContent(value);
  };

  useEffect(() => {
    if (appContext?.selectedEnvironment) {
      (async () => loadMainBranch())();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [appContext?.selectedEnvironment]);

  useEffect(() => {
    if (appContext?.selectedEnvironment) {
      (async () => loadDataEntityFromQueryParams())();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [structures]);

  useEffect(() => {
    context?.setTitle("Data Entity Editor");
    context?.setTargetAPI("environment");
  }, [context]);

  const loadEntity = async (dataStructure: string, id: string) => {
    const dataEntity = await dataPersistenceService.loadDataEntity(dataStructure, id);
    setEntityContent(JSON.stringify(dataEntity, null, 2));
    setEntity(dataEntity);
    setLinkedEntities(getLinkedEntities(dataStructure, dataEntity));

    // add new loaded entity to the history
    if (!history.find((entity) => entity.id === id)) {
      const hist = [...history];
      hist.push({ dataStructure, id });
      setHistory(hist.slice(-historySize));
    }
  };

  const loadButtonClickHandler = async () => {
    if (!selectedStructure || !idValue) {
      return;
    }

    await loadEntity(selectedStructure.name, idValue);
  };

  const loadLinkedEntity = async (linkedEntity: DataEntityLink) => {
    const structure = structures.find((str) => str.name === linkedEntity.dataStructure);
    if (structure) {
      await loadDataEntitiesIds(structure);
      const entityId = linkedEntity.id;
      setIdValue(entityId);
      setInputIdValue(entityId);
      setSelectedStructure(structure);
      await loadEntity(linkedEntity.dataStructure, linkedEntity.id);
    }
  };

  const saveButtonClickHandler = async () => {
    if (selectedStructure)
      try {
        setIsLoading(true);
        await dataPersistenceService.storeDataEntity(selectedStructure.name, JSON.parse(entityContent));
        setAlertMessage("Data saved successfully");
        setAlertSeverity("success");
      } catch (e: any) {
        setAlertMessage(e.debugMessage || "Could not store the data.");
        setAlertSeverity("error");
      } finally {
        setIsLoading(false);
      }
  };

  const addToExportList = async () => {
    if (!selectedStructure || !idValue) {
      return;
    }

    exportIds.push({ id: idValue, name: selectedStructure.name });
    const ids = new Set(exportIds);
    setExportIds(Array.from(ids));
  };

  const exportData = async (): Promise<IDataEntity[]> => {
    if (exportIds.length === 0) {
      return [];
    }

    try {
      setIsLoading(true);
      setShowExportList(false);

      const data = await dataPersistenceService.exportData(exportIds.map((e) => e.id));

      if (data.errors.length > 0) {
        setAlertMessage(`Failed to export some IDs: ${data.errors.join(", ")}`);
        setAlertSeverity("error");
      } else {
        setAlertMessage("Data exported successfully!");
        setAlertSeverity("success");
      }

      return data.list;
    } catch (e: any) {
      setAlertMessage(e.debugMessage || "Could not export the data.");
      setAlertSeverity("error");
    } finally {
      setIsLoading(false);
    }

    return [];
  };

  const importData = async (entities: IDataEntity[]) => {
    setIsLoading(true);
    setShowImportOptions(false);

    try {
      const res = await dataPersistenceService.importData(entities);
      if (res.errors.length > 0) {
        setAlertMessage(`Failed to import some IDs: ${res.errors.join(", ")}`);
        setAlertSeverity("error");
      } else {
        setAlertMessage("Data imported successfully!");
        setAlertSeverity("success");
      }
    } catch (e: any) {
      setAlertMessage(e.debugMessage || "Could not import the data.");
      setAlertSeverity("error");
    } finally {
      setIsLoading(false);
    }
  };

  const archiveOrReviveData = async () => {
    if (selectedStructure && idValue && entity) {
      const operation = entity.archived ? "revive" : "archive";
      try {
        setIsLoading(true);
        if (!selectedStructure.archived) {
          await dataPersistenceService.archiveDataEntity(selectedStructure.name, idValue);
        } else {
          await dataPersistenceService.reviveDataEntity(selectedStructure.name, idValue);
        }

        await loadDataEntitiesIds(selectedStructure);
        setAlertMessage(`Data ${operation}d successfully!`);
        setAlertSeverity("success");
      } catch (error: any) {
        setAlertMessage(error.debugMessage || `Could not ${operation} the data.`);
        setAlertSeverity("error");
      } finally {
        setIsLoading(false);
      }
    }
  };

  const getLinkedEntities = (dataStructure: string, dataEntity: IDataEntity): DataEntityLink[] => {
    return [
      ...getLinkedEntitiesFromDescription(dataStructure, dataEntity),
      ...getLinkedEntitiesFromMap(dataStructure, dataEntity),
    ];
  };

  // get linked entities defined in the description field of the schema
  const getLinkedEntitiesFromDescription = (dataStructure: string, dataEntity: IDataEntity): DataEntityLink[] => {
    const linkedEntities: DataEntityLink[] = [];
    const structure = structures.find((s) => s.name === dataStructure);
    const fields = structure?.schema.properties ?? {};
    Object.entries(fields).forEach(([key, value]) => {
      if (value.description) {
        // match linked data-structure name in a string like "key:DataStructureName.id"
        const regex = /:(\w+)\./;
        const match = value.description.match(regex);

        if (match) {
          const id = Object.entries(dataEntity).find((el) => el[0] === key);
          if (id) {
            linkedEntities.push({ dataStructure: match[1], id: id[1] });
          }
        }
      }
    });

    return linkedEntities;
  };

  // get linked entities defined in the linkedEntitiesMap
  const getLinkedEntitiesFromMap = (dataStructure: string, dataEntity: IDataEntity): DataEntityLink[] => {
    const linkedEntities: DataEntityLink[] = [];

    const mappedEntities = linkedEntitiesMap[dataStructure as keyof typeof linkedEntitiesMap];
    if (!mappedEntities) {
      return [];
    }

    for (const mappedEntity of mappedEntities) {
      const entry = Object.entries(dataEntity).find((el) => el[0] === mappedEntity.field);
      if (entry) {
        const id = entry[1];
        switch (mappedEntity.type) {
          case "string":
            linkedEntities.push({ dataStructure: mappedEntity.dataStructure, id });
            break;
          case "array":
            (id as string[]).map((arrayId) => {
              linkedEntities.push({ dataStructure: mappedEntity.dataStructure, id: arrayId, showId: true });
            });
            break;
          default:
            break;
        }
      }
    }

    return linkedEntities;
  };

  const linkedEntityButonText = (entity: DataEntityLink) => {
    if (entity.showId) {
      return `${entity.dataStructure} (${entity.id})`;
    }

    return entity.dataStructure;
  };

  const openGameServer = () => {
    if (selectedStructure.name === "Neighborhood") {
      const field = Object.entries(entity).find((el) => el[0] === "serverId");
      if (!field) {
        return;
      }
      window.open(`${window.location.origin}/game-servers/?serverId=${field[1]}`);
    }
  };

  return (
    <>
      <Grid container spacing={1} sx={{ marginTop: 1 }}>
        <Grid container justifyContent={"flex-start"}>
          <TextField
            id="datastructure-selector"
            select
            label="Data Structure"
            onChange={(event) => {
              const dataStructure = structures.find((t) => t.name === event.target.value);
              if (dataStructure) {
                setSelectedStructure(dataStructure);
                loadDataEntitiesIds(dataStructure);
              }
            }}
            sx={{
              background: "white",
              minWidth: "240px",
              marginRight: 1,
            }}
            variant="filled"
            value={selectedStructure.name}
          >
            <MenuItem key={defaultDataStructure.name} value={defaultDataStructure.name}>
              {defaultDataStructure.name}
            </MenuItem>
            {structures.map((structure) => (
              <MenuItem key={structure.name} value={structure.name}>
                {structure.name}
              </MenuItem>
            ))}
          </TextField>
          <Autocomplete
            value={idValue}
            id="data-entity-id"
            freeSolo
            options={entitiesIds}
            renderInput={(params) => <TextField {...params} label="Id" sx={{ minWidth: "240px" }} />}
            onChange={(_event, newValue: string | null) => {
              setIdValue(newValue ?? "");
              if (!newValue) {
                setEntityContent("");
                setInputIdValue("");
              }
            }}
            inputValue={inputIdValue}
            onInputChange={(_event, newInputValue) => setInputIdValue(newInputValue)}
          />
          <Button
            variant="contained"
            startIcon={<RefreshIcon />}
            onClick={loadButtonClickHandler}
            sx={{ marginLeft: 1 }}
            disabled={!idValue}
          >
            Load
          </Button>
          <Button
            variant="contained"
            startIcon={<PlaylistAddIcon />}
            onClick={addToExportList}
            sx={{ marginLeft: 1 }}
            disabled={!idValue || exportIds.find((e) => e.id === idValue) !== undefined}
          >
            Add to Export List
          </Button>
          <Button
            variant="contained"
            startIcon={<FileDownloadIcon />}
            onClick={() => setShowExportList(true)}
            sx={{ marginLeft: 1 }}
            disabled={exportIds.length === 0}
          >
            Export
          </Button>
          <Button
            variant="contained"
            startIcon={<PublishIcon />}
            onClick={() => setShowImportOptions(true)}
            sx={{ marginLeft: 1 }}
          >
            Import
          </Button>
          <Button
            variant="contained"
            startIcon={entity?.archived ? <UndoIcon /> : <ArchiveIcon />}
            onClick={() => archiveOrReviveData()}
            sx={{ marginLeft: 1 }}
            disabled={!entity}
          >
            {entity?.archived ? "Revive" : "Archive"}
          </Button>
          <Button
            variant="contained"
            startIcon={<SaveAltIcon />}
            onClick={saveButtonClickHandler}
            size="large"
            disabled={!entityContent}
          >
            Save
          </Button>
        </Grid>
        <div style={{ flexGrow: 1 }} />
        <Grid container>
          <Button
            variant="contained"
            startIcon={<TerminalIcon />}
            onClick={() => setShowRedisTerminal(true)}
            size="large"
            color="secondary"
            sx={{ marginLeft: 1 }}
          >
            Redis
          </Button>
          <Button
            variant="contained"
            startIcon={<ManageSearchIcon />}
            onClick={() => setShowDeepSearch(true)}
            size="large"
            color="secondary"
            sx={{ marginLeft: 1 }}
          >
            Deep Search
          </Button>
          <Button
            variant="contained"
            startIcon={<ContentCopyIcon />}
            onClick={() => setShowCloneEntities(true)}
            size="large"
            color="secondary"
            sx={{ marginLeft: 1 }}
          >
            Clone Data-entities
          </Button>
        </Grid>
        <Grid size={12} sx={{ mt: 2 }}>
          <Breadcrumbs aria-label="breadcrumb">
            {history.map((entity) => {
              return (
                <Chip
                  label={`${entity.dataStructure} - ${entity.id}`}
                  key={`breadcrumb-${entity.dataStructure}-${entity.id}`}
                  onClick={() => loadLinkedEntity(entity)}
                  color="primary"
                  variant={entity.id === idValue ? "filled" : "outlined"}
                />
              );
            })}
          </Breadcrumbs>
        </Grid>
        <Grid size={12} sx={{ mt: 2 }}>
          {selectedStructure.name === "Neighborhood" && idValue && entity && (
            <Button
              key={`game-server-link-${entity.id}`}
              variant="contained"
              startIcon={<StorageIcon />}
              onClick={() => openGameServer()}
              sx={{ marginRight: 1 }}
            >
              Open Game-Server
            </Button>
          )}
          {linkedEntities.map((entity) => (
            <Button
              key={`${entity.dataStructure}-${entity.id}`}
              variant="contained"
              startIcon={<LaunchIcon />}
              onClick={() => loadLinkedEntity(entity)}
              sx={{ marginRight: 1 }}
              disabled={!idValue}
            >
              {linkedEntityButonText(entity)}
            </Button>
          ))}
        </Grid>
        <Grid size={12}>
          <CodeMirror
            value={entityContent}
            width="80vw"
            height="80vh"
            extensions={[json(), tfidHighlightExtension]}
            onChange={codeMirrorChangeHandler}
          />
        </Grid>
      </Grid>
      <Snackbar open={alertMessage !== undefined} autoHideDuration={5000} onClose={alertCloseHandler}>
        <Alert onClose={alertCloseHandler} severity={alertSeverity} sx={{ width: "100%" }}>
          {alertMessage}
        </Alert>
      </Snackbar>
      {isLoading && <LoadingOverlay />}
      <DataExportDialog
        visible={showExportList}
        title="IDs To Export"
        data={exportIds}
        exportData={exportData}
        onDismiss={() => setShowExportList(false)}
      />
      <DataImportDialog
        visible={showImportOptions}
        title="Data Import"
        onAccept={importData}
        onDismiss={() => setShowImportOptions(false)}
      />
      <RedisTerminal visible={showRedisTerminal} onClose={() => setShowRedisTerminal(false)} />
      <DeepSearchDialog visible={showDeepSearch} onClose={() => setShowDeepSearch(false)} />
      <CloneEntitiesDialog visible={showCloneEntities} onClose={() => setShowCloneEntities(false)} />
    </>
  );
};

export default DataEntityEditor;
